import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Apple, Check, LoaderCircle, Play, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";

// Add the published store URLs here after launch. Until then, these render
// as accessible disabled buttons with no navigation behavior.
const appStoreUrl = null;
const googlePlayUrl = null;
const prelaunchSignupEnabled = false;

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const StoreButton = ({ href, icon: Icon, store, downloadLabel }) => {
    const className =
        "flex min-h-16 min-w-[13rem] items-center gap-3 rounded-2xl border border-brand-deep/10 bg-brand-deep px-5 py-3 text-left text-white shadow-[0_12px_30px_rgba(2,27,30,0.12)] transition-transform duration-300";
    const content = (
        <>
            <Icon className="h-7 w-7 shrink-0 text-brand-gold" aria-hidden="true" />
            <span>
                <span className="block font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-white/60">
                    {href ? downloadLabel : "Coming soon"}
                </span>
                <span className="mt-0.5 block text-base font-semibold leading-none">{store}</span>
            </span>
        </>
    );

    if (href) {
        return (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={`${downloadLabel} ${store}`}
                className={`${className} hover:-translate-y-0.5`}
            >
                {content}
            </a>
        );
    }

    return (
        <button
            type="button"
            disabled
            aria-label={`${store} — coming soon`}
            className={`${className} cursor-not-allowed opacity-90`}
        >
            {content}
        </button>
    );
};

export const AppPrelaunch = () => {
    const reduceMotion = useReducedMotion();
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle");
    const [message, setMessage] = useState("");

    const enter = (delay = 0, x = 0, y = 18) => ({
        initial: reduceMotion ? false : { opacity: 0, x, y },
        whileInView: { opacity: 1, x: 0, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
    });

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!prelaunchSignupEnabled) return;

        const normalizedEmail = email.trim().toLowerCase();

        if (!EMAIL_PATTERN.test(normalizedEmail)) {
            setStatus("error");
            setMessage("Please enter a valid email address.");
            return;
        }

        setStatus("submitting");
        setMessage("");

        try {
            const response = await fetch(`${API_BASE_URL}/api/app-prelaunch`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: normalizedEmail }),
            });

            if (!response.ok) {
                throw new Error("Unable to join the app pre-launch list.");
            }

            setStatus("success");
            setMessage("We'll let you know when Reno Genie launches.");
        } catch {
            setStatus("error");
            setMessage("Something went wrong. Please try again.");
        }
    };

    return (
        <section
            id="app-prelaunch"
            data-testid="app-prelaunch-section"
            className="relative overflow-hidden bg-[#FBF8F1] py-24 sm:py-28 lg:py-32"
        >
            <div className="bg-blueprint pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(75%_80%_at_65%_50%,black,transparent)]" />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute right-[8%] top-[12%] h-72 w-72 rounded-full bg-brand-gold/10 blur-3xl"
            />
            <Sparkles
                aria-hidden="true"
                className="pointer-events-none absolute right-[9%] top-[14%] hidden h-6 w-6 text-brand-gold/50 sm:block"
            />

            <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 md:grid-cols-[0.85fr_1.15fr] md:gap-10 lg:gap-20">
                <motion.div
                    {...enter(0, -20, 0)}
                    className="mx-auto flex w-full max-w-[22rem] items-center justify-center md:max-w-[24rem]"
                >
                    <img
                        src="/brand/genie-point.png"
                        alt="Reno Genie presenting the upcoming mobile app"
                        loading="lazy"
                        className="h-auto w-full max-w-[17rem] drop-shadow-[0_24px_32px_rgba(2,27,30,0.16)] sm:max-w-[19rem] lg:max-w-[22rem]"
                    />
                </motion.div>

                <div className="mx-auto w-full max-w-2xl md:mx-0">
                    <motion.p
                        {...enter(0.08)}
                        className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-brand-teal"
                    >
                        The Reno Genie App
                    </motion.p>

                    <motion.h2
                        {...enter(0.14)}
                        className="mt-4 font-display text-4xl font-semibold leading-[1.04] tracking-tighter text-brand-deep sm:text-5xl lg:text-6xl"
                    >
                        Your Renovation Journey, <span className="italic text-brand-teal">Wherever You Go.</span>
                    </motion.h2>

                    <motion.div {...enter(0.2)}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-muted sm:text-lg">
                            Manage your renovation journey from your phone. Stay connected, track your progress, and
                            keep your plans within reach.
                        </p>
                        <p className="mt-4 font-semibold text-brand-deep">The Reno Genie app is coming soon.</p>
                    </motion.div>

                    <motion.div {...enter(0.26)} className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        <StoreButton
                            href={appStoreUrl}
                            icon={Apple}
                            store="App Store"
                            downloadLabel="Download on the"
                        />
                        <StoreButton
                            href={googlePlayUrl}
                            icon={Play}
                            store="Google Play"
                            downloadLabel="Get it on"
                        />
                    </motion.div>

                    <motion.div {...enter(0.32)} className="mt-9 border-t border-brand-deep/10 pt-8">
                        {status === "success" ? (
                            <div
                                role="status"
                                className="flex max-w-xl items-start gap-3 rounded-2xl border border-brand-teal/15 bg-white/80 px-5 py-4 shadow-sm"
                            >
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-teal text-white">
                                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                                </span>
                                <div>
                                    <p className="font-semibold text-brand-deep">You're on the list.</p>
                                    <p className="mt-1 text-sm text-brand-muted">{message}</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="font-semibold text-brand-deep">Be the first to know when we launch.</p>
                                <form onSubmit={handleSubmit} noValidate className="mt-4 max-w-xl">
                                    <label htmlFor="app-prelaunch-email" className="sr-only">
                                        Your email address
                                    </label>
                                    <div className="flex flex-col gap-3 sm:flex-row">
                                        <Input
                                            id="app-prelaunch-email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            inputMode="email"
                                            placeholder="Your email address"
                                            value={email}
                                            onChange={(event) => {
                                                setEmail(event.target.value);
                                                if (status === "error") {
                                                    setStatus("idle");
                                                    setMessage("");
                                                }
                                            }}
                                            disabled={!prelaunchSignupEnabled || status === "submitting"}
                                            aria-invalid={status === "error"}
                                            aria-describedby={message ? "app-prelaunch-message" : undefined}
                                            className="h-14 rounded-full border-brand-deep/15 bg-white px-6 text-base shadow-sm focus-visible:ring-brand-teal"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!prelaunchSignupEnabled || status === "submitting"}
                                            className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-full bg-brand-gold px-7 text-sm font-bold text-brand-deep shadow-[0_12px_28px_rgba(253,185,39,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_34px_rgba(253,185,39,0.34)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-65"
                                        >
                                            {status === "submitting" && (
                                                <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            )}
                                            {status === "submitting"
                                                ? "Joining..."
                                                : prelaunchSignupEnabled
                                                  ? "Notify Me"
                                                  : "Coming soon"}
                                        </button>
                                    </div>
                                    <p
                                        id="app-prelaunch-message"
                                        role={status === "error" ? "alert" : "status"}
                                        className={`mt-3 min-h-5 text-sm ${
                                            status === "error" ? "text-red-700" : "text-brand-muted"
                                        }`}
                                    >
                                        {message}
                                    </p>
                                </form>
                            </>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
